import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { requestTicket } from "@/lib/guest";

interface RatingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  movieId: number;
  movieTitle: string;
  mediaType: 'movie' | 'tv';
  userId?: string;
  currentRating?: number;
}

export const RatingDialog = ({
  isOpen,
  onClose,
  movieId,
  movieTitle,
  mediaType,
  userId,
  currentRating
}: RatingDialogProps) => {
  const [rating, setRating] = useState(currentRating || 0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    if (!userId) {
      requestTicket("rate titles");
      return;
    }

    if (rating === 0) {
      toast({
        title: "Select a rating",
        description: "Please select a rating before saving",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_ratings')
        .upsert({
          user_id: userId,
          tmdb_id: movieId,
          media_type: mediaType,
          rating
        });

      if (error) throw error;

      toast({
        title: "Rating saved",
        description: `You rated "${movieTitle}" ${rating}/10`,
      });

      onClose();
    } catch (error) {
      console.error('Error saving rating:', error);
      toast({
        title: "Error",
        description: "Failed to save rating. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const displayRating = hoveredRating || rating;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rate this {mediaType === 'tv' ? 'show' : 'movie'}</DialogTitle>
          <DialogDescription>
            {movieTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex flex-col items-center gap-4">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                <button
                  key={value}
                  onClick={() => setRating(value)}
                  onMouseEnter={() => setHoveredRating(value)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-8 w-8 ${
                      value <= displayRating
                        ? 'fill-yellow-500 text-yellow-500'
                        : 'text-muted-foreground'
                    }`}
                  />
                </button>
              ))}
            </div>
            
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">
                {displayRating > 0 ? `${displayRating}/10` : 'Select rating'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {displayRating >= 9 && 'Masterpiece'}
                {displayRating >= 7 && displayRating < 9 && 'Great'}
                {displayRating >= 5 && displayRating < 7 && 'Good'}
                {displayRating >= 3 && displayRating < 5 && 'Fair'}
                {displayRating > 0 && displayRating < 3 && 'Poor'}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1"
              disabled={saving || rating === 0}
            >
              {saving ? 'Saving...' : 'Save Rating'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};